import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  TextField,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Button,
  IconButton,
  Typography,
  Grid,
  Divider,
  Dialog,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import dayjs from "dayjs";
import axios from "axios";
import { toast } from "react-toastify";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import debounce from "lodash.debounce";
import DischargeModal from "./DischargeModal";
import DeathReport from "../../../pages/IPD/dashboard/formsReports/death-report";
import CustomAutocomplete from "../../autocomplete-field";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";

const formatDate = (date: dayjs.Dayjs | null) =>
  date ? date.format("DD.MM.YYYY") : "";
const formatTime = (time: dayjs.Dayjs | null) =>
  time ? time.format("hh:mm A") : "";

interface FormData {
  mrn: number;
  date: dayjs.Dayjs;
  patientName: string;
  dateOfAdmission: dayjs.Dayjs;
  guardiansName: string;
  currentDate: dayjs.Dayjs;
  gender: string;
  diagnosis: any;
  causeOfDeath: string;
  deadBodyReceivedBy: string;
  doctorName: string;
  signature: string;
  dateTimeDoc: dayjs.Dayjs;
  dateTimeDeath: dayjs.Dayjs;
  primaryCauseOfDeath: string;
  secondaryCauseOfDeath: string;
  relationWithDiseased: string;
  cnicOfReceiver: string;
  phoneNumberOfReceiver: string;
  department: string;
  age: string;
  weight: string;
  orderedBy?: string; // Optional field for orderedBy

}

interface DeathFormProps {
  patientData?: any;
  recordId?: any;
  fromOPD?: boolean;
  onClose?: () => void;
}

const DeathForm: React.FC<DeathFormProps> = ({
  patientData,
  recordId,
  fromOPD,
  onClose,
}) => {
  const [formData, setFormData] = useState<FormData>({
    mrn: "" as any,
    date: dayjs(),
    patientName: "",
    dateOfAdmission: dayjs(),
    guardiansName: "",
    // motherName: "",
    // spouseName: "",
    currentDate: dayjs(),
    gender: "",
    dateTimeDeath: dayjs(),
    dateTimeDoc: dayjs(),
    diagnosis: "",
    causeOfDeath: "",
    deadBodyReceivedBy: "",
    doctorName: "",
    signature: "",
    primaryCauseOfDeath: "",
    secondaryCauseOfDeath: "",
    relationWithDiseased: "",
    cnicOfReceiver: "",
    phoneNumberOfReceiver: "",
    department: "",
    age: "",
    weight: "",
    orderedBy: "", // Initialize orderedBy
  });
  const [open, setOpen] = useState<boolean>(false);
  const [dataById, setDataById] = useState<any>();
  const [deathStatus, setDeathStatus] = useState<boolean>(false);
  const [deathPrint, setDeathPrint] = useState(false);
  const [deathPrintKey, setDeathPrintKey] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [allDiagnosis, setAllDiagnosis] = useState<any[]>([]);
  const [selectedDiagnosis, setSelectedDiagnosis] = useState<string[]>([]);
  const [searchDiagnosis, setSearchDiagnosis] = useState("");
  const [inputValueDiagnosis, setInputValueDiagnosis] = useState("");
  const location = useLocation();
  const locationState = (location?.state || {}) as any;
  const data = patientData || locationState.data;
  const id = recordId ?? locationState.id;
  const isFromOPD = fromOPD ?? locationState.isFromOPD;
  const navigate = useNavigate();
  const closeForm = () => {
    if (onClose) {
      onClose();
      return;
    }
    navigate("/ipd-dashboard");
  };
  const handleCloseDischarge = () => {
    setOpen(false);
  };

  useEffect(() => {
    // Always seed demographics from the admission/patient row when available.
    // (Previously this only ran when id was missing, so saved death forms
    // often had empty patientName / mrn / doctorName on print.)
    if (!data) return;
    setFormData((prevData: any) => ({
      ...prevData,
      mrn: prevData?.mrn || data?.mrn || 0,
      date: prevData?.date || dayjs(data?.date || undefined),
      patientName: prevData?.patientName || data?.patientName || "",
      dateOfAdmission: prevData?.dateOfAdmission?.isValid?.()
        ? prevData.dateOfAdmission
        : dayjs(
            data?.admissionDateAndTime || data?.dateOfAdmission || undefined
          ),
      guardiansName: prevData?.guardiansName || data?.guardiansName || "",
      currentDate: dayjs(),
      gender:
        prevData?.gender ||
        data?.patientData?.gender ||
        data?.gender ||
        "",
      doctorName: prevData?.doctorName || data?.doctorName || "",
      weight: prevData?.weight || data?.weight || "",
    }));
  }, [data]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleDateChange =
    (name: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      const date = dayjs(value, "YYYY-MM-DD", true);
      if (date.isValid()) {
        setFormData({
          ...formData,
          [name]: date,
        });
      }
    };

  const handleTimeChange =
    (name: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      const time = dayjs(value, "hh:mm A", true);
      if (time.isValid()) {
        setFormData({
          ...formData,
          [name]: time,
        });
      }
    };

  const handleGenderChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      gender: event.target.value,
    });
  };

  const handleArrowClick = async () => {
    try {
      const url = isFromOPD
        ? `${process.env.REACT_APP_IPD_API_URL}/ipd-admission/updateIpdAdmissionStatusById/${data?._id}`
        : `${process.env.REACT_APP_IPD_API_URL}/ipd-admission/updateEmergencyStatusById/${data?._id}`;
      // Replace with your API endpoint
      const requestBody = isFromOPD
        ? { patientStatus: "Death" }
        : { emergencyStatus: "Death" };

      await axios.patch(url, requestBody, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
    } catch (error) {
      console.error("Error sending data:", error);
    }
  };

  useEffect(() => {
    getDataBySearchId();
  }, []);

  const getDataBySearchId = async () => {
    let payload: any = null;
    let fetchedData: any = null;
    try {
      const formName = "DeathForm";
      const response = await axios.get(
        `${process.env.REACT_APP_IPD_API_URL}/ipd-form-data/getFormDataBySearchId/${data?._id}?formName=${formName}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      payload = response?.data?.data || null;
      setDataById(payload);
      // Backend maps patientData by patientId on this response
      fetchedData = payload?.formData || null;
    } catch (error: any) {
      toast.error(error.message);
    }

    const [preSurgical, dischargeForm, referralForm] = await Promise.all([
      fetchDiagnosisForm("Pre_Surgical"),
      fetchDiagnosisForm("DischargeForm"),
      fetchDiagnosisForm("Patient Refferal Form"),
    ]);
    const diagnosisNames = uniqueDiagnosisNames([
      fetchedData?.diagnosis,
      preSurgical?.diagnosis,
      preSurgical?.opd_diagnosis,
      dischargeForm?.diagnosis,
      referralForm?.diagnosis,
      data?.diagnosis,
    ]);
    setSelectedDiagnosis(diagnosisNames);

    const patientFromApi = payload?.patientData || data?.patientData || null;

    if (fetchedData || patientFromApi || data) {
      setFormData((prevData: any) => ({
        ...prevData,
        mrn:
          fetchedData?.mrn ||
          payload?.mrn ||
          patientFromApi?.mrn ||
          data?.mrn ||
          prevData?.mrn,
        patientName:
          fetchedData?.patientName ||
          payload?.patientName ||
          patientFromApi?.patientName ||
          data?.patientName ||
          prevData?.patientName,
        age: fetchedData?.age || prevData?.age,
        weight: fetchedData?.weight || data?.weight || prevData?.weight,
        dateOfAdmission: dayjs(
          fetchedData?.dateOfAdmission ||
            payload?.admissionDateAndTime ||
            data?.admissionDateAndTime ||
            data?.dateOfAdmission
        ),
        date: dayjs(fetchedData?.date || undefined),
        guardiansName:
          fetchedData?.guardiansName ||
          patientFromApi?.guardiansName ||
          data?.guardiansName ||
          prevData?.guardiansName,
        currentDate: dayjs(),
        gender:
          fetchedData?.gender ||
          patientFromApi?.gender ||
          data?.patientData?.gender ||
          data?.gender ||
          prevData?.gender,
        dateTimeDeath: dayjs(
          fetchedData?.dateTimeDeath || fetchedData?.dateOfDeath
        ),
        dateTimeDoc: dayjs(
          fetchedData?.dateTimeDoc || fetchedData?.dateOfDoc
        ),
        diagnosis: diagnosisNames,
        causeOfDeath: fetchedData?.causeOfDeath,
        deadBodyReceivedBy: fetchedData?.deadBodyReceivedBy,
        doctorName:
          fetchedData?.doctorName ||
          payload?.doctorName ||
          data?.doctorName ||
          prevData?.doctorName,
        signature: fetchedData?.signature,
        cnicOfReceiver: fetchedData?.cnicOfReceiver,
        primaryCauseOfDeath: fetchedData?.primaryCauseOfDeath,
        relationWithDiseased: fetchedData?.relationWithDiseased,
        phoneNumberOfReceiver: fetchedData?.phoneNumberOfReceiver,
        secondaryCauseOfDeath: fetchedData?.secondaryCauseOfDeath,
        department: fetchedData?.department,
        orderedBy: fetchedData?.orderedBy || "",
      }));
    }
  };

  const diagnosisNamesFrom = (value: any): string[] => {
    if (!value) return [];
    const splitText = (text: string) =>
      text
        .split(",")
        .map((part) => part.trim())
        .filter(Boolean);
    if (Array.isArray(value)) {
      return value.flatMap((item) => {
        if (typeof item === "string") return splitText(item);
        const name = item?.diagnosisName || item?.name || "";
        return name ? splitText(String(name)) : [];
      });
    }
    if (typeof value === "string") return splitText(value);
    return [];
  };

  const uniqueDiagnosisNames = (values: any[]) =>
    Array.from(new Set(values.flatMap((value) => diagnosisNamesFrom(value))));

  const fetchDiagnosisForm = async (formName: string) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_IPD_API_URL}/ipd-form-data/getFormDataBySearchId/${data?._id}?formName=${formName}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      return response?.data?.data?.formData;
    } catch {
      return null;
    }
  };

  const debouncedGetDiagnosis = useCallback(
    debounce(async (search: string) => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_OPD_API_URL}/diagnosis/getAllSelectAbleDiagnosis?search=${search}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        setAllDiagnosis(response?.data?.data || []);
      } catch (error) {
        console.error(error);
      }
    }, 500),
    []
  );

  useEffect(() => {
    debouncedGetDiagnosis(searchDiagnosis);
  }, [debouncedGetDiagnosis, searchDiagnosis]);

  useEffect(() => {
    setFormData((prevData) => ({
      ...prevData,
      diagnosis: selectedDiagnosis,
    }));
  }, [selectedDiagnosis]);

  const handleSelectChangeDiagnosis = (names: string[]) => {
    const newNames = names.filter((name) => name && !selectedDiagnosis.includes(name));
    setSelectedDiagnosis((prev) => Array.from(new Set([...prev, ...newNames])));
  };

  const handleDeleteChipDiagnosis = (diagnosisToDelete: string) => {
    setSelectedDiagnosis((prev) => prev.filter((item) => item !== diagnosisToDelete));
  };

  const handleCreateNewDiagnosis = async (inputValue: string) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_OPD_API_URL}/diagnosis/createDiagnosis`,
        { diagnosisName: inputValue },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      const newDiagnosis = response?.data?.data;
      const name = newDiagnosis?.diagnosisName || inputValue;
      if (name) {
        setSelectedDiagnosis((prev) => Array.from(new Set([...prev, name])));
        setAllDiagnosis((prev) => [...prev, newDiagnosis || { diagnosisName: name }]);
        setSearchDiagnosis("");
        setInputValueDiagnosis("");
        toast.success(response.data?.message);
      }
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  const isEditingDeath =
    data?.patientStatus === "Death" || data?.emergencyStatus === "Death";

  const saveDeathForm = async () => {
    // All Death Form fields are optional — allow submit with partial data
    return axios.post(
      `${process.env.REACT_APP_IPD_API_URL}/ipd-form-data/CreateFormData`,
      {
        formName: "DeathForm",
        formData: formData,
        searchId: data?._id,
        patientId: data?.patientData?._id,
      },
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      }
    );
  };

  const handleSubmit = async () => {
    if (isSaving) return;
    setIsSaving(true);

    try {
      const response = await saveDeathForm();
      if (!response) return;
      if (isEditingDeath) {
        toast.success(response?.data?.message || "Death form updated");
        closeForm();
        return;
      }
      setDeathStatus(true);
      setOpen(true);
      handleArrowClick();
      toast.success(response.data.success);
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdateAndPrint = async () => {
    if (isSaving) return;
    setIsSaving(true);

    try {
      const response = await saveDeathForm();
      if (!response) return;
      toast.success(response?.data?.message || "Death form updated");
      setDeathPrint(false);
      setDeathPrintKey((key) => key + 1);
      setDeathPrint(true);
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setIsSaving(false);
    }
  };


  const handleDateTimeChange = (formattedDate: any) => {
    if (formattedDate) {
      setFormData({
        ...formData,
        dateTimeDeath: formattedDate,
      });
    }
  };

  const handleDateDocTimeChange = (formattedDate: any) => {
    if (formattedDate) {
      setFormData({
        ...formData,
        dateTimeDoc: formattedDate,
      });
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%",
        maxWidth: "100%",
        background: "#fff",
        borderRadius: "8px",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 1,
          background: "#fff",
          flexShrink: 0,
        }}
      >
        <Typography
          sx={{
            fontWeight: "bold",
            fontSize: "20px",
            color: "#000",
            whiteSpace: "nowrap",

          }}
        >
          Death Form
        </Typography>

        <Box
          sx={{
            display: "flex",
            gap: 0.5,
            justifyContent: "flex-end",
            textAlign: "right",
            fontSize: "12px",
            ml: 3,
          }}
        >
          <Typography sx={{ fontWeight: "600", color: "#000", fontSize: "12px" }}>
            <strong>Name:</strong> {formData.patientName}
          </Typography>

          <Typography sx={{ fontWeight: "600", color: "#000", fontSize: "12px" }}>
            <strong>Guardian:</strong> {formData.guardiansName}
          </Typography>

          <Typography sx={{ fontWeight: "600", color: "#000", fontSize: "12px" }}>
            <strong>MRN:</strong> {formData.mrn}
          </Typography>

          <Typography sx={{ fontWeight: "600", color: "#000", fontSize: "12px" }}>
            <strong>Sex:</strong> {formData.gender}
          </Typography>
          <IconButton
            onClick={closeForm}
            aria-label="Close"
            size="small"
          >
            <CloseIcon />
          </IconButton>
          {/* <Typography sx={{ fontWeight: "600", color: "#000", fontSize: "12px" }}>
  <strong>Age:</strong> {formData.age}
</Typography>

<Typography sx={{ fontWeight: "600", color: "#000", fontSize: "12px" }}>
  <strong>Weight:</strong> {formData.weight}
</Typography> */}



          {/* <Typography sx={{ fontWeight: "600", color: "#000", fontSize: "12px" }}>
            <strong>Date:</strong> {formatDate(formData.date)}
          </Typography>

          <Typography sx={{ fontWeight: "600", color: "#000", fontSize: "12px" }}>
            <strong>Date of Admission:</strong> {formatDate(formData.dateOfAdmission)}
          </Typography> */}
        </Box>
      </Box>
      <Divider />

      <Box sx={{ overflowY: "auto", maxHeight: "calc(100vh - 220px)", p: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={2.4}>
            <Typography sx={{ fontSize: "12px", fontWeight: "500", color: "#000" }}>
              Department
            </Typography>
            <TextField
              placeholder="Department"
              name="department"
              value={formData.department}
              onChange={handleChange}
              variant="outlined"
              fullWidth
              size="small"
              className="search-field"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Typography
              sx={{ fontSize: "12px", fontWeight: "500", color: "#000" }}
            >
              Date & Time Of Death
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DateTimePicker
                value={formData?.dateTimeDeath}
                onChange={(newValue: any) => {
                  handleDateTimeChange(newValue);
                }}
                format="DD.MM.YYYY hh:mm A"
                sx={{
                  "& .MuiInputBase-root": {
                    height: "38px",
                    fontSize: "12px",
                    overflow: "hidden",
                  },
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                    border: "none",
                    backgroundColor: "#F5F5FB",
                  },
                  "& .MuiCalendarPicker-root": {
                    overflow: "hidden",
                  },
                  "& .css-4jnixx-MuiStack-root": {
                    paddingTop: "0px",
                  },
                  width: "100%",
                }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Typography
              sx={{ fontSize: "12px", fontWeight: "500", color: "#000" }}
            >
              Diagnosis
            </Typography>
            <CustomAutocomplete
              multiple
              options={allDiagnosis || []}
              value={selectedDiagnosis || []}
              onChange={(_event, newValue) => {
                handleSelectChangeDiagnosis(
                  (newValue || []).map((diag: any) =>
                    typeof diag === "string" ? diag : diag?.diagnosisName
                  )
                );
              }}
              getOptionLabel={(option) =>
                typeof option === "string" ? option : option?.diagnosisName || ""
              }
              isOptionEqualToValue={(option, value) =>
                (option?.diagnosisName || option) === (value?.diagnosisName || value)
              }
              filterOptions={(options, { inputValue }) =>
                options.filter((option: any) =>
                  option?.diagnosisName
                    ?.toLowerCase()
                    .includes(inputValue.toLowerCase())
                )
              }
              placeholder="Select Diagnosis"
              onTextFieldChange={(event) => {
                setInputValueDiagnosis(event.target.value);
                setSearchDiagnosis(event.target.value);
              }}
              handleBlur={() => {
                setInputValueDiagnosis("");
                setSearchDiagnosis("");
              }}
              onKeyPress={(event) => {
                if (event.key === "Enter" && inputValueDiagnosis.trim()) {
                  event.preventDefault();
                  handleCreateNewDiagnosis(inputValueDiagnosis.trim());
                }
              }}
              handleAddClick={() => {
                if (inputValueDiagnosis.trim()) {
                  handleCreateNewDiagnosis(inputValueDiagnosis.trim());
                }
              }}
            />
            {selectedDiagnosis.length > 0 && (
              <Box
                mt={0.4}
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 0.5,
                  background: "rgba(192, 192, 192, 0.2)",
                  border: "2px solid #D8D8D8",
                  p: 0.5,
                  borderRadius: "12px",
                }}
              >
                {selectedDiagnosis.map((sign) => (
                  <Chip
                    size="small"
                    key={sign}
                    label={sign}
                    onDelete={() => handleDeleteChipDiagnosis(sign)}
                    sx={{
                      borderRadius: "8px",
                      background: "#fff",
                      border: "1px solid rgba(0, 0, 0, 0.22)",
                      fontSize: "12px",
                    }}
                  />
                ))}
              </Box>
            )}
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Typography sx={{ fontSize: "12px", fontWeight: "500", color: "#000" }}>
              Primary Cause of Death
            </Typography>
            <TextField
              placeholder="Primary Cause of Death"
              name="primaryCauseOfDeath"
              value={formData.primaryCauseOfDeath}
              onChange={handleChange}
              variant="outlined"
              fullWidth
              size="small"
              className="search-field"
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2.4}>
            <Typography sx={{ fontSize: "12px", fontWeight: "500", color: "#000" }}>
              Secondary Cause of Death
            </Typography>
            <TextField
              placeholder="Secondary Cause of Death"
              name="secondaryCauseOfDeath"
              value={formData.secondaryCauseOfDeath}
              onChange={handleChange}
              variant="outlined"
              fullWidth
              size="small"
              className="search-field"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Typography
              sx={{ fontSize: "12px", fontWeight: "500", color: "#000" }}
            >
              Dead Body Received By
            </Typography>
            <TextField
              placeholder="Dead Body Received By"
              name="deadBodyReceivedBy"
              value={formData?.deadBodyReceivedBy}
              onChange={handleChange}
              variant="outlined"
              fullWidth
              size="small"
              className="search-field"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2.4}>
            <Typography sx={{ fontSize: "12px", fontWeight: "500", color: "#000" }}>
              Relation with Diseased
            </Typography>
            <TextField
              placeholder="Relation with Diseased"
              name="relationWithDiseased"
              value={formData.relationWithDiseased}
              onChange={handleChange}
              variant="outlined"
              fullWidth
              size="small"
              className="search-field"
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2.4}>
            <Typography sx={{ fontSize: "12px", fontWeight: "500", color: "#000" }}>
              CNIC of Receiver
            </Typography>
            <TextField
              placeholder="CNIC of Receiver"
              name="cnicOfReceiver"
              value={formData.cnicOfReceiver}
              onChange={handleChange}
              variant="outlined"
              fullWidth
              size="small"
              className="search-field"
            />
          </Grid>

          <Grid item xs={12} sm={6} md={2.4}>
            <Typography sx={{ fontSize: "12px", fontWeight: "500", color: "#000" }}>
              Phone Number of Receiver
            </Typography>
            <TextField
              placeholder="Phone Number of Receiver"
              name="phoneNumberOfReceiver"
              value={formData.phoneNumberOfReceiver}
              onChange={handleChange}
              variant="outlined"
              fullWidth
              size="small"
              className="search-field"
            />
          </Grid>
        </Grid>

        <Divider sx={{ mt: 2 }} />
        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid item xs={12} sm={6} md={4}>
            <Typography
              sx={{ fontSize: "12px", fontWeight: "500", color: "#000" }}
            >
              Doctor Name
            </Typography>
            <TextField
              placeholder="Doctor Name"
              name="doctorName"
              value={formData?.doctorName}
              onChange={handleChange}
              variant="outlined"
              fullWidth
              size="small"
              className="search-field"
            />
          </Grid>

          <Grid item xs={12} sm={4} md={4}>
            <Typography
              sx={{ fontSize: "12px", fontWeight: "500", color: "#000" }}
            >
              Date & Time
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DateTimePicker
                value={formData?.dateTimeDoc}
                onChange={(newValue: any) => {
                  handleDateDocTimeChange(newValue);
                }}
                format="DD.MM.YYYY hh:mm A"
                sx={{
                  "& .MuiInputBase-root": {
                    height: "38px",
                    fontSize: "12px",
                    overflow: "hidden",
                  },
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                    border: "none",
                    backgroundColor: "#fff",
                  },
                  "& .MuiCalendarPicker-root": {
                    overflow: "hidden",
                  },
                  "& .css-4jnixx-MuiStack-root": {
                    paddingTop: "0px",
                  },
                  width: "100%",
                }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Typography sx={{ fontSize: "12px", fontWeight: "500", color: "#000" }}>
              Ordered By
            </Typography>
            <TextField
              placeholder="Ordered By"
              name="orderedBy"
              value={formData.orderedBy}
              onChange={handleChange}
              variant="outlined"
              fullWidth
              size="small"
              className="search-field"
            />
          </Grid>

          {/* <Grid item xs={12} sm={6} md={4}>
            <Typography
              sx={{ fontSize: "12px", fontWeight: "500", color: "#000" }}
            >
              Signature
            </Typography>
            <TextField
              placeholder="Signature"
              name="signature"
              value={formData?.signature}
              onChange={handleChange}
              variant="outlined"
              fullWidth
              size="small"
              className="search-field"
            />
          </Grid> */}
        </Grid>
      </Box>
        <Box
          display="flex"
          justifyContent="flex-end"
          gap={1}
          sx={{ flexShrink: 0, p: 2, pt: 1 }}
        >
          <Button
            variant="outlined"
            onClick={closeForm}
            disabled={isSaving}
          >
            Back
          </Button>
          {isEditingDeath && (
            <Button
              variant="contained"
              color="primary"
              disabled={isSaving}
              onClick={handleUpdateAndPrint}
              sx={{ whiteSpace: "nowrap" }}
            >
              Update & Print Death Form
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={isSaving}
          >
            {isEditingDeath ? "Update" : "Submit"}
          </Button>
        </Box>

        <Dialog
          open={open}
          onClose={handleCloseDischarge}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          maxWidth="xl"
          PaperProps={{
            sx: {
              borderRadius: "12px",
            },
          }}
        >
          <DischargeModal
            dischargeData={data}
            handleClose={handleCloseDischarge}
            deathStatus={deathStatus}
            isRefDeathComp={true}
            death={true}
          // isEmergency={isEmergency}
          />
        </Dialog>
        {deathPrint ? (
          <Box
            sx={{
              // Keep in-viewport with tiny opacity so Chrome paints the print source
              // (opacity:0 / clipped nodes often yield a blank print preview).
              position: "fixed",
              left: 0,
              top: 0,
              width: "210mm",
              height: "auto",
              overflow: "visible",
              opacity: 0.01,
              pointerEvents: "none",
              zIndex: -1,
            }}
            aria-hidden
          >
            <DeathReport
              key={deathPrintKey}
              formName={"DeathForm"}
              id={data?._id}
              handleClose={closeForm}
              directPrint={true}
            />
          </Box>
        ) : null}
    </Box>
  );
};

export default DeathForm;
